/**
 * Sheds on concrete hardstanding, behind a steel fence.
 *
 * The second archetype, and the proof the framework holds: it is parameters and nothing else, and
 * registering it edits no stage. Everything that makes it a different place from the suburb is a
 * number or a surface id in this file.
 *
 * The same hundred and sixty metres across as `suburbanRuins`, on purpose. A yard reads as a yard
 * because of its grain, not its size: fewer, bigger plots with long sightlines between them, roads
 * wide enough for something with a trailer, and half again as many loops, so a body crossing it is
 * in the open for longer than it ever is on a suburban street. Holding the extent also holds what a
 * hundred soak seeds cost, which doubles with the archetype count either way.
 */

import type { ZoneArchetype } from '@/sim/worldgen/zoneArchetypeContract';

export const INDUSTRIAL_YARD: ZoneArchetype = {
  id: 'industrialYard',
  label: 'Industrial yard',
  surfaces: {
    // Poured, not grown: the ground of a yard is the slab everything else was bolted to, and it is
    // the first thing that tells the place apart from the dirt a suburb went back to.
    ground: 'weatheredConcrete',
    // Fencing and barrier rather than cast kerb. A palisade around the perimeter and armco down
    // the side of a haul road is what an industrial site edges itself with.
    structure: 'rustedSteel',
    // The one thing the two places share, because a road is a road.
    street: 'crackedAsphalt',
  },
  extent: {
    halfWidth: 80,
    halfDepth: 80,
    // Lower than the suburb's, because nothing here stands above one storey and the ceiling only
    // has to clear the tallest thing the archetype can place.
    ceiling: 10,
  },
  densities: {
    // A sixth of the suburb's rate, against plots several times the area: a yard is a handful of
    // sheds with hardstanding between them, and the empty concrete is the point rather than a gap
    // the stage failed to fill.
    buildings: 0.08,
    // Under a quarter of the suburb's rate, and against more open ground than the suburb has:
    // sparse cover is the trade for the wide roads, and crossing one is a decision here.
    props: 0.6,
    // Under the suburb's, because sightlines are long enough that the same count reads as a
    // crossfire rather than as a patrol.
    enemies: 0.1,
    // Fewer containers than the suburb, and worth more of a walk. A yard is swept, not searched.
    loot: 0.08,
  },
  storeys: {
    // One. A shed is a shed, and the two storey shell with a stair in it is the suburb's shape.
    maxStoreys: 1,
    // Nothing to spend it on at a cap of one, and stated rather than left implied.
    upperStoreyChance: 0,
    // Single-storey sheds: there is no upper floor to be missing.
    openingShare: 0,
    // Nothing stands upstairs in a place with no upstairs.
    upstairsEnemyShare: 0,
    upstairsLootShare: 0,
  },
  relief: {
    amplitudeMetres: 0.35,
    cellMetres: 7,
    dips: 1,
  },
  navigability: {
    // The plan measures a little over nine tenths walkable across every seed tried, which is what
    // a place of few buildings and wide roads comes out at. The band is wide on both sides for the
    // reason the suburb's is: it catches a stage that broke, never a density nudged by a tenth.
    minWalkableFraction: 0.7,
    maxWalkableFraction: 0.99,
  },
  layout: {
    // Coarser than the suburb's, so two or three plots span the zone rather than three or four.
    districtSpanMetres: { min: 52, max: 64 },
    // Half again as wide as a suburban street: two lanes plus the room something with a trailer
    // needs to turn into a plot.
    streetWidthMetres: 12,
    // A compound at the bottom, a whole yard at the top. Above twice the minimum, so no block
    // comes out too long to split and too short to build on.
    lotSpanMetres: { min: 20, max: 44 },
    // Wider than the suburb's, because the fence stands off the hardstanding rather than at the
    // end of somebody's garden.
    edgeBandMetres: 8,
    // Half the streets a spanning tree skipped are built anyway: a yard is a loop road with sheds
    // inside it, and a site with one way around is a site nothing could be driven through.
    loopEdgeFraction: 0.5,
  },
};
